import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { Bookmark } from '@ext-bookmark/shared/types';
import { normalizeUrl, isValidUrl } from '@ext-bookmark/shared/utils';
import { bookmarkSchema, type BookmarkFormData } from '../../lib/validation-schemas';
import { FormError } from '../ui/form-error';

interface BookmarkFormProps {
  bookmark?: Bookmark | null;
  onSave: (bookmark: { name: string; url: string; note?: string }) => void;
  onCancel: () => void;
}

const BookmarkForm: React.FC<BookmarkFormProps> = ({ bookmark, onSave, onCancel }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
  } = useForm<BookmarkFormData>({
    resolver: zodResolver(bookmarkSchema),
    defaultValues: {
      name: bookmark?.name || '',
      url: bookmark?.url || '',
      note: bookmark?.note || '',
    },
  });

  useEffect(() => {
    if (bookmark) {
      reset({
        name: bookmark.name,
        url: bookmark.url,
        note: bookmark.note || '',
      });
    }
  }, [bookmark, reset]);

  const onSubmit = (data: BookmarkFormData) => {
    const normalizedUrl = normalizeUrl(data.url.trim());
    if (isValidUrl(normalizedUrl)) {
      onSave({
        name: data.name.trim(),
        url: normalizedUrl,
        note: data.note?.trim() || undefined,
      });
      if (!bookmark) {
        reset();
      }
    } else {
      setError('url', { type: 'manual', message: '请输入有效的 URL' });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
          名称 <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          {...register('name')}
          className="input-field"
          placeholder="请输入书签名称"
          autoFocus
        />
        <FormError error={errors.name?.message} />
      </div>
      <div>
        <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
          网址 <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          {...register('url')}
          className="input-field"
          placeholder="https://example.com 或 example.com"
        />
        <FormError error={errors.url?.message} />
      </div>
      <div>
        <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
          备注
        </label>
        <textarea
          {...register('note')}
          className="input-field min-h-[80px] resize-none"
          placeholder="可选：添加备注信息"
          rows={3}
        />
        <FormError error={errors.note?.message} />
      </div>
      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="btn-secondary"
        >
          取消
        </button>
        <button
          type="submit"
          className="btn-primary"
        >
          {bookmark ? '保存' : '添加'}
        </button>
      </div>
    </form>
  );
};

export default BookmarkForm;
